import React from 'react';
import ReceiptDivider from './ReceiptDivider';
import ReceiptItem from './ReceiptItem';
import AnimatedConceptDisplay from '../common/AnimatedConceptDisplay';
import { CHAIN_OPTIONS } from '../../constants/networks';
import { CONCEPTS } from '../../constants/concepts';

const Receipt = ({ state, totalPrice }) => {
    const timestamp = new Date().toLocaleString();
    const receiptNumber = Math.floor(Math.random() * 1000000)
        .toString()
        .padStart(6, '0');

    const getConceptName = () =>
        CONCEPTS.find(c => c.id === state.selectedConcept)?.name;

    const getVotedChainName = () =>
        CHAIN_OPTIONS.find(c => c.value === state.selectedChain)?.value;

    const renderSection = (title, fields, data) => (
        <div className="space-y-1">
            <h4 className="text-lg font-medium mb-1">{title}</h4>
            {fields.map((field, index) => (
                <ReceiptItem
                    key={index}
                    label={field.label}
                    value={field.valueKey ? data[field.valueKey] : field.value}
                    highlight={field.highlight}
                />
            ))}
        </div>
    );

    const receiptData = {
        conceptName: getConceptName(),
        votedChainName: getVotedChainName(),
        mintAmount: `${state.mintAmount} × Ξ${state.price}`,
        currentNetwork: state.isConnected ? state.currentNetwork : 'Not Connected',
        totalPrice: `Ξ${totalPrice}`
    };

    const sections = {
        voteDetails: {
            title: 'Vote Details',
            fields: [
                { label: 'Art Style Concept', valueKey: 'conceptName' },
                { label: 'Launch Chain Vote', valueKey: 'votedChainName' }
            ]
        },
        mintDetails: {
            title: 'Mint Details',
            fields: [
                { label: 'Amount', valueKey: 'mintAmount' },
                { label: 'Network', valueKey: 'currentNetwork' }
            ]
        },
        transaction: {
            title: 'Transaction Summary',
            fields: [
                { label: 'Subtotal', valueKey: 'totalPrice' },
                { label: 'Gas Fee', value: 'Est. at transaction', highlight: true }
            ]
        }
    };

    return (
        <div className="bg-white p-3 rounded-lg shadow-lg w-full max-w-sm">
            {/* Header */}
            <div className="text-center mb-2">
                <h3 className="text-xl font-bold">Dæmons Mint Receipt</h3>
                <p className="text-sm text-gray-500">#{receiptNumber}</p>
                <p className="text-sm text-gray-500">{timestamp}</p>
            </div>

            <ReceiptDivider />

            {/* Preview */}
            <div className="py-2">
                <h4 className="text-lg font-medium mb-1">Preview</h4>
                <div className="flex justify-center">
                    <div className="relative w-full aspect-square bg-gray-100 rounded-lg">
                        <AnimatedConceptDisplay selectedConcept={state.selectedConcept} />
                    </div>
                </div>
            </div>

            <ReceiptDivider />

            {/* Vote Details */}
            {renderSection(
                sections.voteDetails.title,
                sections.voteDetails.fields,
                receiptData
            )}

            <ReceiptDivider />

            {/* Mint Details */}
            {renderSection(
                sections.mintDetails.title,
                sections.mintDetails.fields,
                receiptData
            )}

            <ReceiptDivider />

            {/* Transaction Summary */}
            <div className="space-y-1">
                <h4 className="text-lg font-medium mb-1">{sections.transaction.title}</h4>
                <ReceiptItem
                    label="Subtotal"
                    value={`Ξ${totalPrice}`}
                />
                <ReceiptItem
                    label="Gas Fee"
                    value="Est. at transaction"
                    highlight={true}
                />
                <div className="pt-1 mt-1 border-t border-gray-200">
                    <ReceiptItem
                        label="Total Due"
                        value={`Ξ${totalPrice}`}
                        highlight={true}
                    />
                </div>
            </div>

            {/* Footer */}
            <div className="mt-3 pt-2 border-t border-gray-200 text-center text-xs text-gray-500 custom-font">
                Thank you for supporting Dæmons!
            </div>
        </div>
    );
};

export default Receipt;